import React, { useContext, useMemo } from "react";
import { useTextDomainContext, useUser } from "@/app/providers";
import { useCompanies } from "@/hooks";
import { SelectDropdown } from "@/components";
import { getRoleTypeNameFromUser } from "@/utils";
import { SxProps, Theme } from "@mui/material";

interface CustomerDropdownProps {
  componentLabel?: string;
  defaultValue?: string;
  cacheKey?: string;
  showAll?: boolean;
  required?: boolean;
  onChange?: (_event: any) => void;
  visualStyle?: "autoComplete" | "autoCompleteMulti" | "simple" | "old";
  customerType?: "parent" | "child";
  sx?: SxProps<Theme>;
  refetch?: boolean;
  refetched?: () => void;
  currentTenantId?: string;
  fullWidth?: boolean;
  currentTeamId?: string;
}

/**
 * Customer dropdown component
 * @param {object} props props
 * @returns {React.JSX.Element} React Component
 * @class
 */
export function CustomerDropdown(props: CustomerDropdownProps) {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { user } = useUser();
  const roleType = getRoleTypeNameFromUser(user);
  const { currentTenantId, showAll } = props;

  const allCompaniesVars = useMemo(() => {
    return (roleType === "super" || roleType === "system") &&
      currentTenantId === "all"
      ? { customerType: "parent" as const }
      : { customerId: "__none__" };
  }, [roleType, currentTenantId]);

  const userCompaniesVars = useMemo(() => {
    return currentTenantId && currentTenantId !== "all"
      ? { customerType: "parent" as const, tenantId: currentTenantId }
      : { customerId: "__none__" };
  }, [currentTenantId]);

  const { customers: allCompanies } = useCompanies(allCompaniesVars);
  const { customers: userCompanies } = useCompanies(userCompaniesVars);

  //#region useMemo
  const options = useMemo(() => {
    const customerArray: Array<{ label: string; value: string }> = [];
    let selectedCompanies: any[] = [];

    switch (roleType) {
      case "super":
      case "system": {
        if (currentTenantId === "all") {
          selectedCompanies = allCompanies;
        } else if (currentTenantId) {
          selectedCompanies = userCompanies;
        } else {
          selectedCompanies = [];
        }
        break;
      }
      case "partner":
      case "company":
        if (currentTenantId) {
          selectedCompanies = userCompanies;
        } else {
          selectedCompanies = [];
        }
        break;
      default:
        break;
    }

    selectedCompanies.map((customerRow) => {
      const oneCustomer = {
        label: customerRow.name,
        value: customerRow.id,
      };
      customerArray.push(oneCustomer);
      return true;
    });
    return customerArray;
  }, [allCompanies, currentTenantId, roleType, userCompanies]);

  // const defaultValue = useMemo(() => {
  //   let returnValue = "";
  //
  //   if (!returnValue && props?.defaultValue) {
  //     const foundValue = options.find(option => option.value === props?.defaultValue);
  //     if (foundValue) {
  //       returnValue = props.defaultValue;
  //     }
  //   }
  //
  //   if (!returnValue && showAll) {
  //     returnValue = "all";
  //   }
  //
  //   if (!returnValue && options.length) {
  //     returnValue = options[0]?.value;
  //   }
  //
  //   return returnValue;
  // }, [options, props.defaultValue, showAll]);

  const componentLabel = useMemo(() => {
    return props.componentLabel || gettext("Parent Company");
  }, [gettext, props.componentLabel]);
  //#endregion useMemo

  //#region Handlers
  const handleChange = (_event: any) => {
    if (typeof props.onChange === "function") {
      props.onChange(_event);
    }
  };
  //#endregion Handlers

  return (
    <>
      <SelectDropdown
        options={options}
        componentLabel={componentLabel}
        defaultValue={props.defaultValue}
        cacheKey={props.cacheKey}
        showAll={showAll}
        required={props.required}
        onChange={handleChange}
        refetch={props.refetch || false}
        refetched={props.refetched}
        visualStyle="autoComplete"
        sx={{
          ...props.sx,
        }}
      />
    </>
  );
}
